import React, { useState, useEffect, useMemo } from "react";
import { Eye } from "lucide-react";
import {
  DataTable,
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  getDatastreamEventLogs,
  DatastreamEventLogItem,
  EventType,
  EventStatus,
} from "../../../adverity-logs/api";

interface EventLogsTableProps {
  onViewDetails: (eventId: number) => void;
  eventTypeFilter: EventType | "all";
  statusFilter: EventStatus | "all";
  datastreamIdFilter: string;
  onLoadingChange: (loading: boolean) => void;
  onTotalCountChange: (count: number) => void;
  refreshRef: React.MutableRefObject<(() => void) | null>;
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  callback_received: "Callback Received",
  backup_fetch_triggered: "Backup Fetch Triggered",
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch {
    return dateStr;
  }
};

const getStatusBadge = (status: EventStatus) => {
  if (status === "success") {
    return (
      <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300">
        Success
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
        Error
      </Badge>
    );
  }
  return <Badge variant="outline" className="capitalize">{status}</Badge>;
};

const EventLogsTable: React.FC<EventLogsTableProps> = ({
  onViewDetails,
  eventTypeFilter,
  statusFilter,
  datastreamIdFilter,
  onLoadingChange,
  onTotalCountChange,
  refreshRef,
}) => {
  const [eventLogs, setEventLogs] = useState<DatastreamEventLogItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchEventLogs = async () => {
    setLoading(true);
    onLoadingChange(true);
    try {
      const skip = page * rowsPerPage;
      const { items, total_count } = await getDatastreamEventLogs(
        skip,
        rowsPerPage,
        eventTypeFilter === "all" ? undefined : eventTypeFilter,
        statusFilter === "all" ? undefined : statusFilter,
        datastreamIdFilter ? parseInt(datastreamIdFilter, 10) : undefined
      );
      setEventLogs(items);
      setTotalCount(total_count);
      onTotalCountChange(total_count);
    } catch (err) {
      console.error("Error fetching event logs", err);
    } finally {
      setLoading(false);
      onLoadingChange(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [eventTypeFilter, statusFilter, datastreamIdFilter]);

  useEffect(() => {
    fetchEventLogs();
  }, [page, rowsPerPage, eventTypeFilter, statusFilter, datastreamIdFilter]);

  useEffect(() => {
    refreshRef.current = fetchEventLogs;
    return () => { refreshRef.current = null; };
  });

  const columns = useMemo((): TableColumn<DatastreamEventLogItem>[] => [
    {
      key: "sr_no",
      header: "SR NO",
      width: "6%",
      accessor: (row) => row.id,
      render: (_v, _row, index) => (
        <span className="text-gray-500">{index !== undefined ? page * rowsPerPage + index + 1 : ""}</span>
      ),
    },
    {
      key: "event_type",
      header: "EVENT TYPE",
      width: "20%",
      accessor: (row) => row.event_type,
      render: (_v, row) => (
        <span>{EVENT_TYPE_LABELS[row.event_type] || row.event_type}</span>
      ),
    },
    {
      key: "datastream_id",
      header: "DATASTREAM ID",
      width: "14%",
      accessor: (row) => row.datastream_id ?? "-",
    },
    {
      key: "datastream_type_id",
      header: "TYPE ID",
      width: "10%",
      accessor: (row) => row.datastream_type_id ?? "-",
    },
    {
      key: "status",
      header: "STATUS",
      width: "12%",
      align: "center" as const,
      accessor: (row) => row.status,
      render: (_v, row) => (
        <div className="flex justify-center">
          {getStatusBadge(row.status)}
        </div>
      ),
    },
    {
      key: "created_at",
      header: "CREATED AT",
      width: "22%",
      accessor: (row) => row.created_at ?? "",
      render: (_v, row) => formatDateTime(row.created_at),
    },
    {
      key: "details",
      header: "DETAILS",
      width: "8%",
      align: "center" as const,
      accessor: () => "",
      render: (_v, row) => (
        <div className="flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onViewDetails(row.id)}
                >
                  <Eye className="h-4 w-4 text-[#334238]" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>View Details</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ], [page, rowsPerPage, onViewDetails]);

  return (
    <DataTable
      className="flex-1 min-h-0 overflow-auto"
      maxHeight="calc(100vh - 200px)"
      columns={columns}
      data={eventLogs}
      loading={loading}
      emptyMessage="No event logs found."
      stickyHeader
      stickyFooter
      showActions={false}
      pagination={{
        currentPage: page + 1,
        totalPages: Math.max(1, Math.ceil(totalCount / rowsPerPage)),
        pageSize: rowsPerPage,
        totalItems: totalCount,
        onPageChange: (p) => setPage(p - 1),
        onPageSizeChange: (size) => {
          setRowsPerPage(size);
          setPage(0);
        },
        pageSizeOptions: [25, 50, 100],
      }}
    />
  );
};

export default EventLogsTable;
